import React, { useEffect, useState } from 'react';
import * as d3 from 'd3';

import { formatNumber } from '../../lib/utils';

/* Parcel scope hoising is dumb (view with d3 errors) and this hack solves it */
const {
  axisBottom,
  axisLeft,
  select,
  format,
  min,
  max,
  scaleLinear,
  brush
} = d3;

const Datapoint = ({
  x,
  y,
  data,
  fill,
  r,
  isBrushed,
  onMouseOver,
  onMouseOut
}) => {
  const [circleRadius, setCircleRadius] = useState(r);

  // eslint-disable-next-line no-underscore-dangle
  const _onMouseOver = () => {
    setCircleRadius(r * 1.5);
    onMouseOver(x, y, data);
  };
  // eslint-disable-next-line no-underscore-dangle
  const _onMouseOut = () => {
    setCircleRadius(r);
    onMouseOut();
  };

  return (
    <g onMouseOver={_onMouseOver} onMouseOut={_onMouseOut}>
      <circle
        style={{ cursor: 'pointer', pointerEvents: 'all' }}
        cx={x}
        cy={y}
        r={circleRadius}
        fill={isBrushed ? '#05d748' : fill}
        className="datapoint"
      />
    </g>
  );
};

const Axis = ({ type, x, y, label, scale }) => {
  const axis = React.useRef(null);
  const positions = {
    top: { x: scale.range()[1] + 20, y: 0 },
    right: { x: 20, y: 0 },
    bottom: { x: scale.range()[1] / 2.5, y: 30 },
    left: { x: scale.range()[0] / 2.5, y: 40 }
  };

  const labelPosition = positions[type];
  const d3Axis = type === 'left' ? axisLeft : axisBottom;

  useEffect(() => {
    select(axis.current)
      .call(d3Axis(scale).tickFormat(format('~s')))
      .attr('class', `axis axis-${type}`);
  }, [d3Axis, scale, type, axis, x, y, label]);

  const rotate = type === 'left' ? 90 : 0;

  return (
    <g ref={axis} transform={`translate(${x}, ${y})`}>
      <text
        textAnchor="start"
        transform={`rotate(${rotate})`}
        {...labelPosition}
      >
        {label}
      </text>
    </g>
  );
};

const ScatterPlot = ({
  data,
  xLabel = '',
  yLabel = '',
  onBrush = () => {}
}) => {
  const margin = { top: 10, right: 30, bottom: 30, left: 50 };
  const [tooltip, setTooltip] = useState(null);
  const [dimensions, setDimensions] = useState({
    width: 460,
    height: 400
  });

  const svgWidth = dimensions.width - margin.left - margin.right;
  const svgHeight = dimensions.height - margin.top - margin.bottom;

  const svg = React.useRef(null);
  const containerRef = React.useRef(null);
  const brushRef = React.useRef(null);
  const [brushedPoints, setBrushedPoints] = useState(new Set());

  useEffect(() => {
    const resizeObserver = new ResizeObserver(entries => {
      if (entries[0].contentRect.width) {
        setDimensions({
          width: containerRef.current?.offsetWidth,
          height: containerRef.current?.offsetHeight - 32
        });
      }
    });

    resizeObserver.observe(containerRef?.current);
  }, []);

  function isBrushed(brush_coords, cx, cy) {
    const x0 = brush_coords[0][0];
    const x1 = brush_coords[1][0];
    const y0 = brush_coords[0][1];
    const y1 = brush_coords[1][1];
    return x0 <= cx && cx <= x1 && y0 <= cy && cy <= y1; // This return TRUE or FALSE depending on if the points is in the selected area
  }

  const minX = min(data, d => d.x);
  const maxX = max(data, d => d.x);

  const minY = min(data, d => d.y);
  const maxY = max(data, d => d.y);

  const xScale = scaleLinear().range([0, svgWidth]).domain([minX, maxX]);
  const yScale = scaleLinear().range([svgHeight, 0]).domain([minY, maxY]);

  const points = data.map(d => {
    return { x: xScale(d.x), y: yScale(d.y), index: d.id, data: d };
  });

  useEffect(() => {
    if (brushRef.current) {
      const svgNode = select(brushRef.current);
      svgNode.selectAll('.d3brush').remove();

      svgNode
        .append('g')
        .attr('class', 'd3brush')
        .call(
          brush() // Add the brush feature using the d3.brush function
            .extent([
              [0, 0],
              [svgWidth, svgHeight]
            ])
            .on('start brush end', e => {
              const { selection } = e;
              if (selection === null) {
                setBrushedPoints(new Set());
                onBrush(null, null, []);
                return;
              }
              const brushedPoints = new Set(
                points
                  .filter(({ x, y }) => isBrushed(selection, x, y))
                  .map(({ index }) => index)
              );

              const xRange = [
                xScale.invert(selection[0][0]),
                xScale.invert(selection[1][0])
              ];

              const yRange = [
                yScale.invert(selection[0][1]),
                yScale.invert(selection[1][1])
              ];

              setBrushedPoints(brushedPoints);
              const range = {
                x: xRange,
                y: yRange
              };
              onBrush(range, selection, brushedPoints);
            }) // Each time the brush selection changes, trigger the 'updateChart' function
        );
    }
  }, [dimensions]);
  useEffect(() => {
    if (!containerRef.current) return;

    const container = select(containerRef.current);
    const tooltipNode = container.selectAll('.scatterplot-tooltip');

    if (!tooltip) {
      tooltipNode.style('opacity', 0);
      return;
    }

    const html = `
      Address: ${tooltip.data.address} <br />
      Project cost: $${formatNumber(tooltip.data.x)}
      <br />
      Payback period: ${formatNumber(tooltip.data.y)} years
    `;

    tooltipNode.transition().duration(150).style('opacity', 0.9);
    tooltipNode
      .html(html)
      .style(
        'transform',
        `translate(${tooltip.x - 100}px,${tooltip.y - 30}px)`
      );

    console.log('tooltipNode', tooltipNode);
  }, [tooltip, containerRef]);

  const translate = `translate(${margin.left},${margin.top})`;
  return (
    <div
      className="container"
      style={{
        height: '100%',
        width: '100%',
        position: 'relative'
      }}
      ref={containerRef}
    >
      <div
        className="scatterplot-tooltip"
        style={{
          position: 'absolute',
          pointerEvents: 'none',
          padding: '10px',
          backgroundColor: 'white',
          boxShadow: '0 0 5px rgba(0,0,0,0.3)',
          borderRadius: '5px',
          zIndex: 1000
        }}
      />
      <svg
        style={{
          position: 'absolute',
          left: 0,
          top: 0
        }}
        className="d3svg"
        ref={svg}
        width={svgWidth + margin.left + margin.right}
        height={svgHeight + margin.top + margin.bottom}
      >
        <g transform={translate}>
          <g ref={brushRef} className="brush" />
          {points.map(({ x, y, index, data }) => (
            <Datapoint
              xScale={xScale}
              yScale={yScale}
              isBrushed={brushedPoints.has(index)}
              onMouseOver={(x, y, data) => {
                setTooltip({
                  x,
                  y,
                  data
                });
              }}
              onMouseOut={() => {
                setTooltip(null);
              }}
              containerRef={containerRef}
              key={`${x}-${y}-${index}`}
              data={data}
              x={x}
              y={y}
              fill="#0070FF"
              r={5}
            />
          ))}
          <Axis label={xLabel} x={0} y={0} scale={yScale} type="left" />
          <Axis
            label={yLabel}
            x={0}
            y={svgHeight}
            scale={xScale}
            type="bottom"
          />
        </g>
      </svg>
    </div>
  );
};

export default ScatterPlot;
